import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonSearchbar,
  IonToolbar,
  useIonAlert,
  useIonViewWillEnter,
} from "@ionic/react";
import { radioButtonOnOutline, radioButtonOffOutline } from "ionicons/icons";
import { useCallback, useContext, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { formatDateUIDate } from "../util/UIHelper";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import useStandardRequests, {
  StandardRequestGroup,
  StandardRequestRow,
} from "../hooks/work-order/useStandardRequests";
import "./SelectStandardRequest.css";

const RANK_LABEL_FALLBACK: Record<string, string> = {
  lbl_sr_same_eq: "Same Equipment",
  lbl_sr_same_eqclass: "Same Equipment Class",
  lbl_other_sr: "Other",
};

// Fields the searchbar matches against (all optional on a query row).
const SEARCH_FIELDS: (keyof StandardRequestRow)[] = [
  "cf_work_order_0",
  "description_0",
  "equipment_0",
  "equipment_class_0",
  "wo_type_0",
];

const matchesSearch = (row: StandardRequestRow, needle: string) =>
  SEARCH_FIELDS.some((f) =>
    String(row[f] ?? "")
      .toLowerCase()
      .includes(needle)
  );

const SelectStandardRequest: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { equipId } = useParams<{ equipId?: string }>();
  const displayEquipId = equipId ? decodeParam(equipId) : "";

  const { getStandardRequests } = useStandardRequests();

  const [groups, setGroups] = useState<StandardRequestGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedSr, setSelectedSr] = useState("");
  // Per-group open state, keyed by "rank-<n>". Each group renders in its own
  // IonAccordionGroup (see below) so they expand/collapse fully independently —
  // a single shared IonAccordionGroup, even with `multiple`, was coupling them.
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const label = useCallback(
    (key: string) => translations[key] || RANK_LABEL_FALLBACK[key] || key,
    [translations]
  );

  const loadStandardRequests = useCallback(async () => {
    setLoading(true);
    setErrorText("");
    try {
      const result = await getStandardRequests(displayEquipId);
      if (!result.success) {
        const msg = result.messages?.[0]?.Text;
        setGroups([]);
        setErrorText(
          msg ||
            translatedMessages["NOSRFound"]?.MessageText ||
            "No standard requests found."
        );
        return;
      }
      setGroups(result.groups);
      // Default-expand the top-ranked visible groups (matches Web's
      // groupDefaultExpanded = 2, i.e. first two group levels open).
      const initialOpen: Record<string, boolean> = {};
      result.groups.forEach((g, i) => {
        initialOpen[`rank-${g.rank}`] = i < 2;
      });
      setOpenMap(initialOpen);
    } catch {
      setGroups([]);
      setErrorText(
        translatedMessages["NOSRFound"]?.MessageText ||
          "No standard requests found."
      );
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, [displayEquipId, getStandardRequests, translatedMessages]);

  useIonViewWillEnter(() => {
    setSelectedSr("");
    setSearchText("");
    loadStandardRequests();
  });

  // Apply the searchbar filter while preserving grouping: filter items inside
  // each group and drop groups that end up empty.
  const filteredGroups = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((r) => matchesSearch(r, needle)) }))
      .filter((g) => g.items.length > 0);
  }, [groups, searchText]);

  const totalVisible = useMemo(
    () => filteredGroups.reduce((sum, g) => sum + g.items.length, 0),
    [filteredGroups]
  );

  const onCreate = () => {
    if (!selectedSr) return;
    presentAlert({
      header: translations["lbl_standard_request"] || "Standard Request",
      message: (
        translatedMessages["MobConfirmCreateWoFromSr"]?.MessageText ||
        "Create a work order from standard request @1?"
      ).replace("@1", selectedSr),
      buttons: [
        { text: translations["lbl_btn_cancel"] || "Cancel", role: "cancel" },
        {
          text: translations["lbl_btn_ok"] || "OK",
          handler: () => {
            history.push(
              `/standard-request/${encodeParam(selectedSr)}/equipment/${encodeParam(
                displayEquipId
              )}/create`
            );
          },
        },
      ],
    });
  };

  const renderRow = (row: StandardRequestRow) => {
    const sr = row.cf_work_order_0;
    const meta = [
      row.equipment_0,
      row.equipment_class_0,
      row.wo_type_0,
      row.entered_date_0 ? formatDateUIDate(row.entered_date_0) : "",
    ]
      .filter(Boolean)
      .join(" • ");
    const isSelected = selectedSr === sr;
    return (
      // Selection is handled manually (no IonRadioGroup) so the accordion
      // groups' ionChange events can't be mistaken for a selection change.
      <IonItem
        key={sr}
        className="sr-row"
        button
        detail={false}
        onClick={() => setSelectedSr(sr)}
      >
        <IonIcon
          slot="start"
          color={isSelected ? "primary" : "medium"}
          icon={isSelected ? radioButtonOnOutline : radioButtonOffOutline}
        />
        <IonLabel className="ion-text-wrap">
          <h3 className="sr-row-title">{sr}</h3>
          {row.description_0 && <p className="sr-row-desc">{row.description_0}</p>}
          {meta && <IonNote className="sr-row-meta">{meta}</IonNote>}
        </IonLabel>
      </IonItem>
    );
  };

  return (
    <IonPage>
      <PageHeader
        title={
          displayEquipId
            ? `${translations["lbl_category_equipment"] || "Equipment"} ${displayEquipId}`
            : translations["lbl_standard_request"] || "Standard Request"
        }
        subtitle={
          translations["lbl_standard_request"] || "Standard Request"
        }
        showBack
      />
      <IonContent fullscreen>
        <IonLoading isOpen={loading} message={translations["lbl_loading"] || "Loading..."} />

        <IonSearchbar
          value={searchText}
          debounce={200}
          placeholder={translations["lbl_filter_results"] || "Filter results"}
          onIonInput={(e) =>
            setSearchText((e.target as HTMLIonSearchbarElement).value ?? "")
          }
        />

        {loaded && !loading && totalVisible === 0 && (
          <div className="sr-empty">
            <IonNote>
              {errorText ||
                translatedMessages["NOSRFound"]?.MessageText ||
                "No standard requests found."}
            </IonNote>
          </div>
        )}

        {filteredGroups.map((group) => {
          const key = `rank-${group.rank}`;
          return (
              // Each group is its own accordion group so toggling one never
              // collapses another.
              <IonAccordionGroup
                key={key}
                value={openMap[key] ? key : undefined}
                onIonChange={(e) =>
                  setOpenMap((m) => ({ ...m, [key]: e.detail.value === key }))
                }
              >
                <IonAccordion value={key}>
                  <IonItem slot="header" color="light">
                    <IonLabel>
                      {label(group.labelKey)}{" "}
                      <IonNote>({group.items.length})</IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonList slot="content">{group.items.map(renderRow)}</IonList>
                </IonAccordion>
              </IonAccordionGroup>
            );
          })}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              fill="solid"
              routerDirection="back"
              routerLink={
                displayEquipId
                  ? `/Equipment/${encodeParam(displayEquipId)}`
                  : "/home"
              }
            >
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton fill="solid" disabled={!selectedSr} onClick={onCreate}>
              {translations["lbl_btn_create"] || "Create"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  SelectStandardRequest,
  "SelectStandardRequest"
);
